import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import ConsumerHeader from "@/components/ConsumerHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Switch } from "@/components/ui/switch";
import { Trash2, MessageSquare } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useLinkCoupon, useUserCoupons, useRemoveCoupon } from "@/hooks/useUserCoupons";

interface ProfileFormData {
  firstName: string;
  lastName: string;
  email: string;
  dateOfBirth: string;
  gender: string;
  phoneNumber: string;
  smsNotificationsEnabled: boolean;
}

interface EmergencyContactFormData {
  emergencyContactName: string;
  emergencyContactPhone: string;
  emergencyContactRelationship: string;
}

interface PasswordFormData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

// Helper function to format UK phone numbers
const formatPhoneNumber = (phoneNumber: string): string => {
  if (!phoneNumber) return phoneNumber;
  
  // Remove all non-digit characters
  const digitsOnly = phoneNumber.replace(/\D/g, '');
  
  // If it starts with 07 and is 11 digits, convert to +44
  if (digitsOnly.startsWith('07') && digitsOnly.length === 11) {
    return '+44' + digitsOnly.substring(1);
  }
  
  // If it already starts with +44, keep as is
  if (phoneNumber.startsWith('+44')) {
    return phoneNumber;
  }
  
  // Return original if it doesn't match UK mobile pattern
  return phoneNumber;
};

const Profile = () => {
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [isUpdatingEmergencyContact, setIsUpdatingEmergencyContact] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const [profileData, setProfileData] = useState<any>(null);

  const { user, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  
  // Use the hooks
  const { data: userCoupons, isLoading: couponsLoading } = useUserCoupons();
  const linkCouponMutation = useLinkCoupon();
  const removeCouponMutation = useRemoveCoupon();

  const profileForm = useForm<ProfileFormData>({
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      dateOfBirth: "",
      gender: "",
      phoneNumber: "",
      smsNotificationsEnabled: true,
    },
  });

  const emergencyContactForm = useForm<EmergencyContactFormData>({
    defaultValues: {
      emergencyContactName: "",
      emergencyContactPhone: "",
      emergencyContactRelationship: "",
    },
  });

  const passwordForm = useForm<PasswordFormData>({
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth", { replace: true });
    }
  }, [user, authLoading, navigate]);

  // Load user data into form
  useEffect(() => {
    const loadUserData = async () => {
      if (user) {
        // Get profile data from the profiles table
        const { data: profile } = await supabase
          .from('profiles')
          .select('date_of_birth, gender, phone_number, sms_notifications_enabled, emergency_contact_name, emergency_contact_phone, emergency_contact_relationship')
          .eq('id', user.id)
          .single();

        setProfileData(profile);

        profileForm.reset({
          firstName: user.user_metadata?.first_name || "",
          lastName: user.user_metadata?.last_name || "",
          email: user.email || "",
          dateOfBirth: profile?.date_of_birth || "",
          gender: profile?.gender || "",
          phoneNumber: profile?.phone_number || "",
          smsNotificationsEnabled: profile?.sms_notifications_enabled ?? true,
        });

        emergencyContactForm.reset({
          emergencyContactName: profile?.emergency_contact_name || "",
          emergencyContactPhone: profile?.emergency_contact_phone || "",
          emergencyContactRelationship: profile?.emergency_contact_relationship || "",
        });
      }
    };

    loadUserData();
  }, [user, profileForm, emergencyContactForm]);

  const onSubmitProfile = async (data: ProfileFormData) => {
    setIsUpdatingProfile(true);
    setError("");
    setMessage("");

    try {
      // Format phone number before saving
      const formattedPhoneNumber = formatPhoneNumber(data.phoneNumber);

      const { error } = await supabase.auth.updateUser({
        email: data.email,
        data: {
          first_name: data.firstName,
          last_name: data.lastName,
          date_of_birth: data.dateOfBirth,
          gender: data.gender,
        }
      });

      if (error) {
        setError(error.message);
      } else {
        // Also update the profiles table with formatted phone number
        const { error: profileError } = await supabase
          .from('profiles')
          .update({
            first_name: data.firstName,
            last_name: data.lastName,
            date_of_birth: data.dateOfBirth,
            gender: data.gender,
            phone_number: formattedPhoneNumber,
            sms_notifications_enabled: data.smsNotificationsEnabled,
          })
          .eq('id', user?.id);

        if (profileError) {
          console.error('Profile update error:', profileError);
        }

        setMessage("Profile updated successfully!");
      }
    } catch (err) {
      console.error("Profile update error:", err);
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  const onSubmitEmergencyContact = async (data: EmergencyContactFormData) => {
    setIsUpdatingEmergencyContact(true);
    setError("");
    setMessage("");

    try {
      // Format emergency contact phone number before saving
      const formattedEmergencyPhone = formatPhoneNumber(data.emergencyContactPhone);

      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          emergency_contact_name: data.emergencyContactName,
          emergency_contact_phone: formattedEmergencyPhone,
          emergency_contact_relationship: data.emergencyContactRelationship,
        })
        .eq('id', user?.id);

      if (profileError) {
        setError(profileError.message);
      } else {
        setMessage("Emergency contact updated successfully!");
      }
    } catch (err) {
      console.error("Emergency contact update error:", err);
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setIsUpdatingEmergencyContact(false);
    }
  };

  const onSubmitPassword = async (data: PasswordFormData) => {
    setIsUpdatingPassword(true);
    setError("");
    setMessage("");

    if (data.newPassword !== data.confirmPassword) {
      setError("New passwords do not match.");
      setIsUpdatingPassword(false);
      return;
    }

    try {
      const { error } = await supabase.auth.updateUser({
        password: data.newPassword
      });

      if (error) {
        setError(error.message);
      } else {
        setMessage("Password updated successfully!");
        passwordForm.reset();
      }
    } catch (err) {
      console.error("Password update error:", err);
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  const handleLinkCoupon = async () => {
    if (!couponCode.trim()) {
      toast({
        title: "Enter a coupon code.",
        description: "Please provide a valid coupon code.",
        variant: "destructive",
      });
      return;
    }

    linkCouponMutation.mutate(couponCode, {
      onSuccess: () => {
        setCouponCode("");
      },
    });
  };

  const handleRemoveCoupon = (userCouponId: string, couponName: string) => {
    if (confirm(`Are you sure you want to remove the coupon "${couponName}"?`)) {
      removeCouponMutation.mutate(userCouponId);
    }
  };

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Don't render if no user
  if (!user) {
    return null;
  }

  // Check if date of birth and gender are already filled
  const hasDateOfBirth = profileData?.date_of_birth;
  const hasGender = profileData?.gender;

  return (
    <div className="min-h-screen bg-gray-50">
      <ConsumerHeader />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          {/* Success/Error Messages */}
          {message && (
            <Alert>
              <AlertDescription className="text-sm text-green-600">{message}</AlertDescription>
            </Alert>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertDescription className="text-sm">{error}</AlertDescription>
            </Alert>
          )}

          {/* Profile Information Card */}
          <Card className="bg-white">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-gray-900">
                Profile Information
              </CardTitle>
              <p className="text-sm text-black">
                Update your account's profile information and email address.
              </p>
            </CardHeader>
            <CardContent>
              <Form {...profileForm}>
                <form onSubmit={profileForm.handleSubmit(onSubmitProfile)} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={profileForm.control}
                      name="firstName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-gray-700">
                            First Name
                          </FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={profileForm.control}
                      name="lastName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-gray-700">
                            Last Name
                          </FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={profileForm.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-gray-700">
                          Email
                        </FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            type="email"
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Only show date of birth and gender if they haven't been filled */}
                  {(!hasDateOfBirth || !hasGender) && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {!hasDateOfBirth && (
                        <FormField
                          control={profileForm.control}
                          name="dateOfBirth"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-medium text-gray-700">
                                Date of Birth
                              </FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  type="date"
                                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      )}

                      {!hasGender && (
                        <FormField
                          control={profileForm.control}
                          name="gender"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-medium text-gray-700">
                                Gender
                              </FormLabel>
                              <Select onValueChange={field.onChange} value={field.value}>
                                <FormControl>
                                  <SelectTrigger className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500">
                                    <SelectValue placeholder="Select your gender" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="male">Male</SelectItem>
                                  <SelectItem value="female">Female</SelectItem>
                                  <SelectItem value="other">Other</SelectItem>
                                  <SelectItem value="prefer_not_to_say">Prefer not to say</SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      )}
                    </div>
                  )}

                  <FormField
                    control={profileForm.control}
                    name="phoneNumber"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-gray-700">
                          Phone Number
                        </FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            type="tel"
                            placeholder="e.g., 07507780613 or +447507780613"
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                          />
                        </FormControl>
                        <p className="text-xs text-gray-500 mt-1">
                          UK mobile numbers starting with 07 will be automatically formatted to +44
                        </p>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={profileForm.control}
                    name="smsNotificationsEnabled"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between p-3 border rounded-lg">
                        <div className="space-y-0.5">
                          <FormLabel className="text-sm font-medium flex items-center gap-2">
                            <MessageSquare className="h-4 w-4" />
                            SMS Notifications
                          </FormLabel>
                          <p className="text-xs text-gray-500">
                            Receive SMS alerts for booking confirmations and reminders
                          </p>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            disabled={isUpdatingProfile}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <Button
                    type="submit"
                    disabled={isUpdatingProfile}
                    className="bg-gray-800 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded-md"
                  >
                    {isUpdatingProfile ? "Saving..." : "SAVE"}
                  </Button>
                </form>
              </Form>
            </CardContent>
          </Card>

          {/* Coupon Management Card */}
          <Card className="bg-white">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                Coupon Management
              </CardTitle>
              <p className="text-sm text-black">
                Link new coupons and view your active coupons.
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Link new coupon */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Link a New Coupon</h3>
                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-end">
                  <div className="flex-1 w-full">
                    <Label htmlFor="coupon-code" className="text-sm font-medium text-gray-700">
                      Coupon Code
                    </Label>
                    <Input
                      id="coupon-code"
                      type="text"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value)}
                      placeholder="Enter coupon code"
                      autoComplete="off"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <Button
                    disabled={linkCouponMutation.isPending}
                    onClick={handleLinkCoupon}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-5 py-2 rounded-md"
                  >
                    {linkCouponMutation.isPending ? "Linking..." : "Link Coupon"}
                  </Button>
                </div>
              </div>

              {/* Active coupons list */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Your Active Coupons</h3>
                {couponsLoading ? (
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-2 text-sm text-gray-500">Loading coupons...</p>
                  </div>
                ) : userCoupons && userCoupons.length > 0 ? (
                  <div className="grid gap-4">
                    {userCoupons
                      .filter((userCoupon) => userCoupon.coupon) // Filter out coupons with null coupon data
                      .map((userCoupon) => (
                      <div key={userCoupon.id} className="border rounded-lg p-4 bg-gray-50">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900">{userCoupon.coupon.name}</h4>
                            <p className="text-sm text-gray-600 mt-1">
                              Code: <span className="font-mono font-medium">{userCoupon.coupon.code}</span>
                            </p>
                            {userCoupon.coupon.description && (
                              <p className="text-sm text-gray-600 mt-1">{userCoupon.coupon.description}</p>
                            )}
                            <p className="text-sm text-gray-600 mt-1">
                              Discount: {userCoupon.coupon.discount_type === 'percentage' ? `${userCoupon.coupon.discount_value}%` : `£${userCoupon.coupon.discount_value}`}
                            </p>
                            <p className="text-xs text-gray-500 mt-2">
                              Used {userCoupon.times_used} times
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Active
                            </span>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleRemoveCoupon(userCoupon.id, userCoupon.coupon.name)}
                              disabled={removeCouponMutation.isPending}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <p>No active coupons found.</p>
                    <p className="text-sm mt-1">Link a coupon above to get started!</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Emergency Contact Card */}
          <Card className="bg-white">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-gray-900">
                Emergency Contact
              </CardTitle>
              <p className="text-sm text-black">
                Provide emergency contact information for safety purposes.
              </p>
            </CardHeader>
            <CardContent>
              <Form {...emergencyContactForm}>
                <form onSubmit={emergencyContactForm.handleSubmit(onSubmitEmergencyContact)} className="space-y-6">
                  <FormField
                    control={emergencyContactForm.control}
                    name="emergencyContactName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-gray-700">
                          Emergency Contact Name
                        </FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder="Full name of emergency contact"
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={emergencyContactForm.control}
                    name="emergencyContactPhone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-gray-700">
                          Emergency Contact Phone
                        </FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            type="tel"
                            placeholder="e.g., 07507780613 or +447507780613"
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                          />
                        </FormControl>
                        <p className="text-xs text-gray-500 mt-1">
                          UK mobile numbers starting with 07 will be automatically formatted to +44
                        </p>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={emergencyContactForm.control}
                    name="emergencyContactRelationship"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-gray-700">
                          Relationship
                        </FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500">
                              <SelectValue placeholder="Select relationship" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="parent">Parent</SelectItem>
                            <SelectItem value="spouse">Spouse</SelectItem>
                            <SelectItem value="partner">Partner</SelectItem>
                            <SelectItem value="sibling">Sibling</SelectItem>
                            <SelectItem value="child">Child</SelectItem>
                            <SelectItem value="friend">Friend</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button
                    type="submit"
                    disabled={isUpdatingEmergencyContact}
                    className="bg-gray-800 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded-md"
                  >
                    {isUpdatingEmergencyContact ? "Saving..." : "SAVE"}
                  </Button>
                </form>
              </Form>
            </CardContent>
          </Card>

          {/* Update Password Card */}
          <Card className="bg-white">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-gray-900">
                Update Password
              </CardTitle>
              <p className="text-sm text-black">
                Ensure your account is using a long, random password to stay secure.
              </p>
            </CardHeader>
            <CardContent>
              <Form {...passwordForm}>
                <form onSubmit={passwordForm.handleSubmit(onSubmitPassword)} className="space-y-6">
                  <FormField
                    control={passwordForm.control}
                    name="currentPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-gray-700">
                          Current Password
                        </FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            type="password"
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={passwordForm.control}
                    name="newPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-gray-700">
                          New Password
                        </FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            type="password"
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={passwordForm.control}
                    name="confirmPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-gray-700">
                          Confirm Password
                        </FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            type="password"
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button
                    type="submit"
                    disabled={isUpdatingPassword}
                    className="bg-gray-800 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded-md"
                  >
                    {isUpdatingPassword ? "Saving..." : "SAVE"}
                  </Button>
                </form>
              </Form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Profile;
